import { ClockCircleOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Col, Modal, Row, Spin, Table, Tag, message } from "antd";
import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { makeApiRequest } from "../../shared/api";
import styles from "./billing.module.scss";
import { useNavigate } from 'react-router-dom';
import PriceCard from "./components/price";
import PriceGauge from "./components/meter/index";
import BalanceLiquid from "./components/liquid/index";

const STRIPE_KEY = "pk_live_51QBrHeCEoVTYWzcpD0ilphRMPyJ65sEzKQdkr2mh6R12Bi9RtWmEXLAHWjiT3z1jK9dbQSAbI30fCb268x8M7X6B00Ij5n6qdo";
const stripePromise = loadStripe(STRIPE_KEY);

export default function BillingsUI({
  balanceQuota,
  billingList,
  status,
  isLoading,
  isModalVisible,
  handleOk,
}) {
  const [loadingStates, setLoadingStates] = useState({});
  const [quotaBalance, setQuotaBalance] = useState(0);
  const [fetchingQuota, setFetchingQuota] = useState(true);
  const [activities, setActivities] = useState([]);
  const [isQuotaModal, setIsQuotaModal] = useState(false);
  const [fetchingActivities, setFetchingActivities] = useState(false);
  const [plans, setPlans] = useState([]);
  const [priceGaugeKey, setPriceGaugeKey] = useState(0);
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const navigate = useNavigate();

  const fetchActivities = React.useCallback(async () => {
    try {
      setFetchingActivities(true);
      const response = await makeApiRequest("GET", "billing/activities");
      
      // Handle the new response structure
      if (response?.status === 'success' && response?.data?.activities) {
        setActivities(response.data.activities);
      } else {
        console.error('Invalid activities response structure');
        setActivities([]);
      }
    } catch (error) {
      console.error("Failed to fetch billing activities:", error);
      message.error("Failed to load billing activities");
      setActivities([]);
    } finally {
      setFetchingActivities(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  useEffect(() => {
    const fetchQuota = async () => {
      try {
        setFetchingQuota(true);
        const response = await makeApiRequest('GET', 'billing/quota',);
        console.log('Quota response:', response);
        if (response && response.pending_quota !== undefined) {
          setQuotaBalance(response.pending_quota);
          setPriceGaugeKey(prev => prev + 1);

          if (response.pending_quota <= 0) {
            setIsQuotaModal(true);
          }
        } else {
          throw new Error('Invalid quota response');
        }
      } catch (error) {
        console.error('Failed to fetch quota:', error);
        message.error('Failed to load available chats balance');
      } finally {
        setFetchingQuota(false);
      }
    };

    fetchQuota();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setFetchingPlans(true);
        const response = await makeApiRequest("GET", "billing/plans");

        if (response && Array.isArray(response.plans)) {
          // Filter out free plan and transform remaining plans
          const transformedPlans = response.plans
            .filter((plan) => plan.plan_id !== "plan_free") // Remove free plan
            .map((plan) => ({
              plan: plan.plan_name,
              chats: plan.Chat_limit,
              payment_plan: `PAY_${plan.plan_id.split("_")[1]}_USD`,
              price: parseInt(
                plan.plan_name.split("$")[1]?.split(" ")[0] || "0"
              ),
              tag: plan.plan_name.includes("49 - 500 chats")
                ? "Most Popular"
                : "",
              features: [plan.Plan_Description],
              isFree: false, // All plans here are paid plans
              buttonText: "Buy Now",
            }));

          setPlans(transformedPlans);
        } else {
          throw new Error("Invalid plans response");
        }
      } catch (error) {
        console.error("Failed to fetch plans:", error);
        message.error("Failed to load pricing plans");
      } finally {
        setFetchingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  const handleBuyNow = async (paymentPlan, index) => {
    try {
      setLoadingStates(prev => ({ ...prev, [index]: true }));

      const currentDomain = process.env.REACT_APP_DOMAIN || window.location.origin;
      console.log('Current domain:', currentDomain);
      
  
      const response = await makeApiRequest('POST', 'billing/payment', {
        payment_plan: paymentPlan,
        success_url: `${currentDomain}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${currentDomain}/cancel`
      });
      console.log('Checkout response:', response);
  
      if (!response?.status === 'success' || !response?.data?.id) {
        throw new Error('Invalid checkout session response');
      }
  
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Failed to initialize Stripe');
      }
  
      console.log('Redirecting to checkout with session:', response.data.id);
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.id
      });
  
      if (error) {
        throw error;
      }
  
    } catch (error) {
      console.error('Payment flow error:', error);
      message.error('Failed to initiate payment: ' + error.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, [index]: false }));
    }
  };

  const activityColumns = [
    {
      title: "Date",
      dataIndex: "timestamp",
      key: "timestamp",
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      defaultSortOrder: 'descend',
      render: (timestamp) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    {
      title: "Type",
      dataIndex: "activity_type",
      key: "activity_type",
      render: (type) => (
        <Tag color={type === "QUOTA_CONSUMED" ? "orange" : "green"}>
          {type.replace("_", " ")}
        </Tag>
      ),
    },
    {
      title: "Quota",
      dataIndex: "quota",
      key: "quota",
    },
    {
      title: "Details",
      dataIndex: "details",
      key: "details",
      ellipsis: true,
    },
  ];

  if (status === 'loading' || fetchingQuota || fetchingPlans) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          Billing - Usage Report & Add-on
        </h1>
      </div>
      <div className={styles.quotaInfo}>
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} md={8}>
            <div className={styles.gaugeWrapper}>
              <PriceGauge 
              key={priceGaugeKey}
              initialBalance={quotaBalance}
              loading={fetchingQuota}
              />
            </div>
          </Col>
          <Col xs={32} md={12}>
            {/* <div className={styles.liquidWrapper}>
              <BalanceLiquid balance={quotaBalance} />
            </div> */}
            <div className={styles.billingActivities}>
              <h3>Billing Activities</h3>
              {fetchingActivities ? (
                <Spin size="small" />
              ) : activities && activities.length > 0 ? (
                <Table
                  dataSource={activities}
                  columns={activityColumns}
                  rowKey="activity_id"
                  pagination={{ pageSize: 3 }}
                />
              ) : (
                <div className={styles.noActivities}>
                  <p>No billing activities found</p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <div className={styles.header}>
        <p className={styles.introText}>
          Ready to level up your productivity?
          <br />
          <span className={styles.highlightText}>
            Pick a plan below and get started with personalized AI assistance
            today.
          </span>
        </p>
      </div>

      <Row gutter={[24, 24]} justify="center" className={styles.row}>
        {plans.map((item, index) => (
          <Col
            key={index}
            xs={24}
            sm={24}
            md={12}
            lg={6}
            className={styles.cardWrapper}
          >
            <div className={`${styles.card} ${styles["card-hover"]}`}>
              <PriceCard
                chats={item.chats}
                plan={item.plan}
                paymentPlan={item.payment_plan}
                price={item.price}
                features={item.features}
                tag={item.tag}
                isLoading={loadingStates[index] || false}
                index={index}
                handleBuyNow={handleBuyNow}
                disabled={item.disabled || item.isFree}
                buttonText={item.buttonText}
              />
            </div>
          </Col>
        ))}
      </Row>

      <Modal
        title="Quota Exhausted"
        open={isQuotaModal}
        onOk={() => setIsQuotaModal(false)}
        onCancel={() => setIsQuotaModal(false)}
      >
        <p>Your quota has been exhausted. Please purchase additional credits to continue using the service.</p>
      </Modal>
    </div>
  );
}